/**
 * Form pieces in the simulator's type scale: 12px gray labels over 32px,
 * 8px-radius controls with 13px text, one hover and focus treatment for inputs
 * and selects alike.
 */

import { useState, type ComponentProps, type ReactNode } from 'react';
import { Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PillButton } from './pill-button';
import { humanize } from './text';

const CONTROL = cn(
  'h-8 rounded-lg border-gray-500 bg-white px-2.5 text-[13px] text-text-body shadow-none transition-colors',
  'placeholder:text-text-disabled hover:border-gray-600',
  'focus-visible:border-gray-600 focus-visible:ring-[1.95px] focus-visible:ring-gray-500'
);

export const Field = ({ label, className, children }: { label: string; className?: string; children: ReactNode }) => (
  <label className={cn('flex min-w-0 flex-col gap-1', className)}>
    <span className='text-xs leading-4 text-gray-800'>{label}</span>
    {children}
  </label>
);

export const FieldInput = ({ className, ...props }: ComponentProps<typeof Input>) => (
  <Input className={cn(CONTROL, className)} {...props} />
);

const UNSET = '__unset__';

type FieldOption = string | { value: string; label: string };

const optionValue = (o: FieldOption) => (typeof o === 'string' ? o : o.value);
const optionLabel = (o: FieldOption) => (typeof o === 'string' ? humanize(o) : o.label);

interface FieldSelectProps {
  value: string;
  options: FieldOption[];
  onChange: (value: string) => void;
  /** Label of the empty choice; omit it to require a value. */
  unsetLabel?: string;
  placeholder?: string;
  disabled?: boolean;
  'aria-label'?: string;
}

// An empty value binds to the unset item, so that row reads as the selected one
// in the open menu and the trigger shows its label muted. With no unset item to
// bind to, '' stays '': Radix's own signal to show the placeholder instead.
export const FieldSelect = ({ value, options, onChange, unsetLabel, placeholder, disabled, 'aria-label': ariaLabel }: FieldSelectProps) => (
  <Select
    value={value || (unsetLabel ? UNSET : '')}
    onValueChange={(v) => onChange(v === UNSET ? '' : v)}
    disabled={disabled}
  >
    <SelectTrigger
      size='sm'
      aria-label={ariaLabel}
      className={cn(CONTROL, 'w-full data-[placeholder]:text-text-disabled', !value && 'text-text-disabled')}
    >
      <SelectValue placeholder={placeholder ?? unsetLabel} />
    </SelectTrigger>
    <SelectContent>
      {unsetLabel && (
        <SelectItem value={UNSET} className='text-[13px] text-muted-foreground'>
          {unsetLabel}
        </SelectItem>
      )}
      {options.map((o) => (
        <SelectItem key={optionValue(o)} value={optionValue(o)} className='text-[13px]'>
          {optionLabel(o)}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

/**
 * Optional fields kept behind a dashed "+ Label" pill until asked for, so a form opens on its
 * essentials; it starts open when the group already holds values. Several sit in a
 * `flex flex-wrap gap-2` row, where an open one takes the full width. One-way on purpose:
 * taking fields away mid-edit is not a kindness, and a read-only section uses a disclosure.
 */
export const RevealGroup = ({ label, filled, children }: { label: string; filled: boolean; children: ReactNode }) => {
  const [open, setOpen] = useState(filled);
  if (!open) {
    return (
      <PillButton dashed onClick={() => setOpen(true)}>
        <Plus className='h-3.5 w-3.5' />
        {label}
      </PillButton>
    );
  }
  return (
    <div className='order-first flex w-full basis-full flex-col gap-2.5 pt-1'>
      <span className='text-xs leading-4 font-medium text-text-body'>{label}</span>
      {children}
    </div>
  );
};
